import { useState } from 'react';
import styled from 'styled-components';
import { List16, Location16 } from '@elfsight/icons';
import { FreeLink, pickFreeLinkSettings } from '@elfsight/widget-free-link';
import { usePortalNestProvider } from '@elfsight/widget-modal';
import { FloatingButton } from '@elfsight/widget-ui';
import { getInvertedColor } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../meta';
import { Map } from '../components/map';
import { DirectoryHeader } from '../directory/directory-header';
import { DirectoryLocationsList } from '../directory/directory-locations-list';
import { LocationCardPopup } from '../location-card/location-card-popup';

export function MobileLayout() {
  const settings = useSettings();
  const { t } = useLingui();
  const freeLinkSettings = pickFreeLinkSettings(settings);
  const [anchorElement, setAnchorElement] = useState<Element | null>(null);
  const [displayDirectory, setDisplayDirectory] = useState(true);
  const portalNest = usePortalNestProvider();

  const handleDisplayToggle = () => {
    setDisplayDirectory((prev) => !prev);
  };

  return (
    <Container ref={setAnchorElement}>
      <Directory _hideMap={displayDirectory}>
        <DirectoryHeader />
        {displayDirectory && (
          <DirectoryLocationsList
            bottomSpacerSize={64}
            previewWithActions={false}
            detailedCard={settings.detailCardEnabled}
          />
        )}
      </Directory>

      <MapContainer _hideMap={displayDirectory}>
        <Map />
      </MapContainer>

      <LocationCardPopup
        style={{ height: '100%' }}
        margin={0}
        withBackdrop
        fullscreen
        fixed
        mobileMode
        closeOnOutsideClick
        portalContainerElement={portalNest || document.body}
      />

      <FloatingButton
        onClick={handleDisplayToggle}
        containerStyle={{ position: 'absolute', bottom: 0 }}
        color={settings.accentColor}
        fontColor={getInvertedColor(settings.accentColor)}
        portalContainerElement={anchorElement ?? undefined}
        position={{
          position: 'bottom',
          verticalOffset: 24,
          horizontalOffset: 0
        }}
        iconLeft={displayDirectory ? Location16 : List16}
        font={{
          fontWeight: 'normal',
          fontSize: 15,
          ...settings.textStyle
        }}
      >
        {displayDirectory ? t`View on Map` : t`View List`}
      </FloatingButton>

      <FreeLink
        {...freeLinkSettings}
        appAlias={meta.appAlias}
        appName={meta.appName}
        style={{
          link: {
            position: 'absolute',
            zIndex: 99999999999,
            transform: 'rotate(270deg)',
            transformOrigin: '100% 100%',
            bottom: '50%',
            left: 'initial',
            right: '8px'
          }
        }}
      />
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  height: 100%;
  max-height: calc(100vh - 40px);
  padding-bottom: env(safe-area-inset-bottom);
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Directory = styled.div<{ _hideMap: boolean }>`
  height: ${({ _hideMap }) => (_hideMap ? '100%' : 'auto')};
  width: 100%;
  display: flex;
  flex-direction: column;
  pointer-events: none;
  z-index: 2;
`;

const MapContainer = styled.div<{ _hideMap: boolean }>`
  ${({ _hideMap }) => _hideMap && 'display: none;'}
  height: 100%;
  width: 100%;
  position: relative;
  z-index: 1;
`;
